// PATH: src/pages/admin/tabs/DevotionalsTab.jsx
// VERSION: 1.1 — CRUD Devocionales con theme brand, tags y hovers

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  useBreakpointValue,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiSave,
  FiPlusCircle,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';



export default function DevotionalsTab() {
  const toast = useToast();

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const tagBorder = useColorModeValue('brand.300', 'border.default');
  const tagBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
        borderColor: useColorModeValue('brand.400', 'brand.300'),
      },
    },
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({
    slug: '',
    title: '',
    date: '',
    author: '',
    tags: '',
    verse_reference: '',
    verse_text: '',
    summary: '',
    content: '',
    serie: '',
    _originalSlug: null,
  });

  const tagList = useMemo(
    () =>
      Array.from(
        new Set(
          (form.tags || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ),
    [form.tags],
  );

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        '/devotionals?q=' + encodeURIComponent(q) + '&limit=1000',
      );
      setItems(data?.items || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando devocionales' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [q]);

  function resetForm() {
    setForm({
      slug: '',
      title: '',
      date: '',
      author: '',
      tags: '',
      verse_reference: '',
      verse_text: '',
      summary: '',
      content: '',
      serie: '',
      _originalSlug: null,
    });
  }

  function editRow(r) {
    setForm({
      slug: r.slug || '',
      title: r.title || '',
      date: r.date || '',
      author: r.author || '',
      tags: (r.tags || []).join(', '),
      verse_reference: r.verse?.reference || '',
      verse_text: r.verse?.text || '',
      summary: r.summary || '',
      content: (r.content || []).join('\n'),
      serie: r.serie || '',
      _originalSlug: r.slug || null,
    });
  }

  async function save() {
    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      date: form.date || null,
      author: form.author || null,
      tags: tagList,
      verse_reference: form.verse_reference || null,
      verse_text: form.verse_text || null,
      summary: form.summary || null,
      serie: form.serie || null,
      content: (form.content || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    if (!payload.slug || !payload.title) {
      toast({ status: 'warning', title: 'Slug y título son requeridos' });
      return;
    }
    try {
      if (form._originalSlug) {
        await axiosInstance.put(
          `/devotionals/${encodeURIComponent(form._originalSlug)}`,
          {
            ...payload,
          },
        );
        toast({ status: 'success', title: 'Devocional actualizado' });
      } else {
        await axiosInstance.post('/devotionals', payload);
        toast({ status: 'success', title: 'Devocional creado' });
      }
      resetForm();
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(slug) {
    if (!window.confirm('¿Eliminar devocional?')) return;
    try {
      await axiosInstance.delete(`/devotionals/${encodeURIComponent(slug)}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>
        Devocionales
      </Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Administra devocionales diarios o semanales con título, autor, verso base y contenido.
      </Box>

      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder={useBreakpointValue({
            base: 'Buscar…',
            md: 'Buscar por título, autor, versículo, tag...',
          })}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button
          {...bw.btnGhost}
          leftIcon={<FiRefreshCw />}
          onClick={load}
        >
          Refrescar
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={2} mb={2}>
        <Input
          {...bw.input}
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Título"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Fecha (YYYY-MM-DD)"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Autor"
          value={form.author}
          onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Serie (Opcional)"
          value={form.serie}
          onChange={(e) => setForm((f) => ({ ...f, serie: e.target.value }))}
        />
      </SimpleGrid>
      <Input
        {...bw.input}
        placeholder="Tags (coma separada)"
        mb={2}
        value={form.tags}
        onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
      />
      <Wrap mb={2}>
        {tagList.map((t, i) => (
          <WrapItem key={i}>
            <Tag border="1px solid" borderColor="brand.300" bg="brand.50">
              <TagLabel>{t}</TagLabel>
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={2}>
        <Input
          {...bw.input}
          placeholder="Verso — referencia"
          value={form.verse_reference}
          onChange={(e) =>
            setForm((f) => ({ ...f, verse_reference: e.target.value }))
          }
        />
        <Input
          {...bw.input}
          placeholder="Verso — texto"
          value={form.verse_text}
          onChange={(e) =>
            setForm((f) => ({ ...f, verse_text: e.target.value }))
          }
        />
      </SimpleGrid>
      <Textarea
        {...bw.input}
        placeholder="Resumen"
        rows={2}
        mb={2}
        value={form.summary}
        onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
      />
      <Textarea
        {...bw.input}
        placeholder="Contenido (una línea por párrafo)"
        rows={6}
        mb={2}
        value={form.content}
        onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
      />
      <Flex gap={2} mb={4} wrap="wrap">
        <Button
          {...bw.btn}
          leftIcon={form._originalSlug ? <FiSave /> : <FiPlusCircle />}
          onClick={save}
        >
          {form._originalSlug ? 'Actualizar' : 'Crear'}
        </Button>
        {form._originalSlug && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg="brand.100">
              <Tr>
                <Th borderColor="brand.200">Slug</Th>
                <Th borderColor="brand.200">Título</Th>
                <Th borderColor="brand.200">Fecha</Th>
                <Th borderColor="brand.200">Autor</Th>
                <Th borderColor="brand.200">Serie</Th>
                <Th borderColor="brand.200">Tags</Th>
                <Th borderColor="brand.200">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.slug} _hover={{ bg: 'brand.50' }}>
                  <Td borderColor="brand.200" fontWeight="bold">
                    {r.slug}
                  </Td>
                  <Td borderColor="brand.200">{r.title}</Td>
                  <Td borderColor="brand.200">{r.date || ''}</Td>
                  <Td borderColor="brand.200">{r.author || ''}</Td>
                  <Td borderColor="brand.200">{r.serie || ''}</Td>
                  <Td borderColor="brand.200">
                    {(r.tags || []).join(', ')}
                  </Td>
                  <Td borderColor="brand.200" whiteSpace="nowrap">
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(r)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(r.slug)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {items.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4} borderColor="brand.200">
                    No hay devocionales registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
